import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader, EmptyState } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, PlusCircle, Sparkles, Save, Loader2, Trash2, History } from 'lucide-react';
import { POLICY_CATEGORIES } from '@/lib/format';

export default function PoliciesPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('leave');
  const [drafting, setDrafting] = useState(false);

  const load = async () => {
    const r = await api.get(`/policies?company_id=${companyId}`);
    setPolicies(r.data);
    if (r.data.length && !selected) setSelected(r.data[0]);
  };

  useEffect(() => { api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)); load(); }, [companyId]);
  useEffect(() => { if (selected) { setContent(selected.content || ''); setTitle(selected.title || ''); setCategory(selected.category || 'leave'); } }, [selected?.id]);

  const create = async () => {
    const label = POLICY_CATEGORIES.find((p) => p.value === category)?.label || 'Policy';
    try {
      const r = await api.post('/policies', { company_id: companyId, category, title: `${label} · ${company?.name}`, content: '' });
      toast.success('Policy created'); await load(); setSelected(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const draft = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const r = await api.post('/ai/draft-policy', { company_id: companyId, category: selected.category, company_name: company?.name });
      setContent(r.data.content);
      toast.success('AI draft ready - edit and save.');
    } catch (e) { toast.error('AI failed'); }
    finally { setDrafting(false); }
  };

  const save = async () => {
    if (!selected) return;
    try {
      const r = await api.patch(`/policies/${selected.id}`, { title, category, content });
      setSelected(null);  // Close editor and show list
      toast.success('Saved'); load();
    } catch (e) { toast.error('Save failed'); }
  };

  const del = async () => {
    if (!selected || !window.confirm('Delete policy?')) return;
    await api.delete(`/policies/${selected.id}`); setSelected(null); load(); toast.success('Deleted');
  };

  return (
    <>
      <PageHeader
        title={`${company?.name || '…'} · HR Policies`}
        description="Company-specific HR policies. AI-drafted, human-edited, versioned on every save."
        actions={<Dialog><DialogTrigger asChild><Button data-testid="add-policy-button"><PlusCircle className="h-4 w-4 mr-2" />New policy</Button></DialogTrigger><DialogContent><DialogHeader><DialogTitle>New policy</DialogTitle><DialogDescription>Pick a category. Empty draft will be created - you can AI-generate later.</DialogDescription></DialogHeader><div className="space-y-3"><Label>Category</Label><Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="policy-category-select"><SelectValue /></SelectTrigger><SelectContent>{POLICY_CATEGORIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div><DialogFooter><Button onClick={create} data-testid="policy-create-button">Create</Button></DialogFooter></DialogContent></Dialog>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6 grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className="space-y-2">
          {policies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No policies yet. Create one to start.</p>
          ) : policies.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} className={`w-full text-left rounded-lg border p-3 hover:bg-accent transition-colors ${selected?.id === p.id ? 'bg-accent border-primary/40' : 'bg-card'}`} data-testid={`policy-item-${p.id}`}>
              <p className="text-sm font-medium truncate">{p.title}</p>
              <p className="text-xs text-muted-foreground capitalize">{p.category.replaceAll('_',' ')} · v{p.version}</p>
            </button>
          ))}
        </div>
        <div>
          {!selected ? (
            <EmptyState icon={FileText} title="Select a policy" description="Pick one from the left or create a new policy to start drafting." />
          ) : (
            <Card className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="policy-title-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Category</Label><Select value={category} onValueChange={setCategory}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{POLICY_CATEGORIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Content (Markdown)</Label>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={draft} disabled={drafting} data-testid="policy-ai-draft">{drafting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI draft</Button>
                    <Button size="sm" onClick={save} data-testid="policy-save-button"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
                    <Button size="sm" variant="ghost" onClick={del} data-testid="policy-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <Textarea rows={20} value={content} onChange={(e) => setContent(e.target.value)} className="font-mono text-sm max-w-[80ch]" data-testid="policy-content-textarea" />
              </div>
              {selected?.history?.length > 0 && (
                <div className="rounded-lg border bg-muted/40 p-3">
                  <p className="text-xs font-semibold flex items-center gap-1"><History className="h-3 w-3" />Version history</p>
                  <ul className="text-xs mt-2 space-y-1">
                    {selected.history.map((h, i) => <li key={i} className="text-muted-foreground">v{h.version} — {new Date(h.saved_at).toLocaleString()} · {h.actor_name}</li>)}
                  </ul>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
